import { PlusOutlined } from "@ant-design/icons";
import { Button, Flex, List, Select, Table, Typography } from "antd";
import moment from "moment-timezone";
import config from "app/envConfig";
import { capitalizeFirstLetter, formatDate } from "common/utils";
import UploadDocumentModal from "features/documentsTable/consumerPortal/components/uploadDocumentModal";
import {
  // useDeleteConsumerPortalDebtorDocumentMutation,
  useGetConsumerPortalDebtorDocumentsQuery,
} from "features/documentsTable/consumerPortal/documentsAPI";
import useIsMobile from "hooks/useIsMobile";
import { useState } from "react";
import styled from "styled-components";

const StyledList = styled(List)`
  margin-top: 20px;
  border-radius: 8px;
  overflow: hidden;
  .ant-list-item {
    background: white;
    padding: 12px;
  }
  .ant-list-footer {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const ListTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const ListSubtitle = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
`;

const ViewFileLink = styled.a`
  color: #1677ff;
  font-size: 14px;
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  text-align: right;
  text-decoration: none;
  &:hover {
    color: #4096ff;
  }
  &:active {
    color: #0958d9;
  }
`;

const DocumentLinksContainer = styled(Flex)`
  min-width: 0;
  flex: 1;
  justify-content: flex-end;
  align-items: flex-start;
  max-width: 100%;
`;

const LinkWrapper = styled.div`
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-align: right;
`;

const StyledSelect = styled(Select)`
  flex: 1;
`;

const ListItemFlex = styled(Flex)`
  min-width: 0;
`;

const LeftSectionFlex = styled(Flex)`
  flex-shrink: 0;
`;

const HeaderFlex = styled(Flex)`
  margin-bottom: 12px;
  margin-top: 12px;
`;

const HeaderTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 0;
`;

const StyledTable = styled(Table)`
  cursor: pointer;
`;

function DocumentsTable() {
  const [isUploadDocumentModalVisible, setUploadDocumentModalVisible] = useState(false);
  const isMobile = useIsMobile();
  const { data: documents, isLoading: isDocumentsLoading } =
    useGetConsumerPortalDebtorDocumentsQuery();
  // const [deleteConsumerPortalDocument] = useDeleteConsumerPortalDebtorDocumentMutation();

  // const handleDelete = async (payload) => {
  //   const result = await deleteConsumerPortalDocument(payload);
  //   if ("data" in result) {
  //     message.success("Document removed successfully!");
  //   }
  // };

  const onUploadDocumentOk = () => {
    setUploadDocumentModalVisible(false);
  };

  const onUploadDocumentCancel = () => {
    setUploadDocumentModalVisible(false);
  };

  const columns = [
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => formatDate(record.createdAt),
      sorter: {
        compare: (a, b) => {
          const dateA = a.createdAt;
          const dateB = b.createdAt;
          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => {
        const tokens = text.split("_").map((val) => {
          return capitalizeFirstLetter(val);
        });
        const prettifiedText = tokens.join(" ");
        return <div>{prettifiedText}</div>;
      },
      sorter: (a, b) => {
        return a.type.toLowerCase() > b.type.toLowerCase();
      },
    },
    {
      title: "File Name",
      dataIndex: "filename",
      key: "filename",
      render: (text, record) => {
        return record.file ? (
          <a target="_blank" href={new URL(record.file, config.apiBaseUrl).href} rel="noreferrer">
            {text}
          </a>
        ) : (
          text
        );
      },
    },
    {
      title: "External URL",
      dataIndex: "externalUrl",
      key: "externalUrl",
      width: 200,
      render: (externalUrl) => {
        return externalUrl ? (
          <a target="_blank" href={externalUrl} rel="noreferrer">
            {externalUrl}
          </a>
        ) : null;
      },
    },
    // {
    //   title: "Actions",
    //   dataIndex: "actions",
    //   key: "actions",
    //   render: (text, record) => {
    //     return (
    //       <Popconfirm
    //         placement="topLeft"
    //         okText="Yes"
    //         title="Are you sure you want to delete this document?"
    //         onConfirm={() =>
    //           handleDelete({
    //             uuid: record.uuid,
    //             type: record.type,
    //           })
    //         }
    //       >
    //         <DeleteOutlined key="delete" />
    //       </Popconfirm>
    //     );
    //   },
    // },
  ];

  return (
    <>
      <HeaderFlex align="center">
        <HeaderTitle>Documents</HeaderTitle>
        <Button
          type="link"
          onClick={() => setUploadDocumentModalVisible(true)}
          icon={<PlusOutlined />}
        >
          Add Document
        </Button>
      </HeaderFlex>
      {isMobile ? (
        <MobileDocumentsView documents={documents} isLoading={isDocumentsLoading} />
      ) : (
        <StyledTable
          bordered
          loading={isDocumentsLoading}
          // @ts-ignore
          columns={columns}
          dataSource={documents}
          scroll={{ x: "max-content" }}
        />
      )}
      <UploadDocumentModal
        title="Add Document"
        open={isUploadDocumentModalVisible}
        onOk={onUploadDocumentOk}
        onCancel={onUploadDocumentCancel}
      />
    </>
  );
}

function MobileDocumentsView({ documents, isLoading }) {
  const [sortDate, setSortDate] = useState(null);
  const [sortType, setSortType] = useState(null);

  const handleDateSortChange = (value) => {
    setSortDate(value);
    setSortType(null);
  };

  const handleTypeSortChange = (value) => {
    setSortType(value);
    setSortDate(null);
  };

  // Sort documents based on selected sort field and order
  const sortedDocuments = documents
    ? [...documents].sort((a, b) => {
        // Priority: date sort first, then type sort
        if (sortDate) {
          const comparison = moment(a.createdAt).diff(moment(b.createdAt));
          const dateResult = sortDate === "asc" ? comparison : -comparison;
          if (dateResult !== 0) {
            return dateResult;
          }
        }

        if (sortType) {
          const comparison = a.type.toLowerCase() > b.type.toLowerCase() ? 1 : -1;
          return sortType === "asc" ? comparison : -comparison;
        }

        return 0;
      })
    : [];

  const formatDocumentType = (text) => {
    const tokens = text.split("_").map((val) => {
      return capitalizeFirstLetter(val);
    });
    return tokens.join(" ");
  };

  const renderDocumentLink = (document) => {
    const hasFile = !!document.file;
    const hasExternalUrl = !!document.externalUrl;

    if (!hasFile && !hasExternalUrl) {
      return <Typography.Text type="secondary">No file</Typography.Text>;
    }

    return (
      <>
        {hasFile && (
          <LinkWrapper>
            <ViewFileLink
              target="_blank"
              href={new URL(document.file, config.apiBaseUrl).href}
              rel="noreferrer"
            >
              {document.filename || document.file}
            </ViewFileLink>
          </LinkWrapper>
        )}
        {hasExternalUrl && (
          <LinkWrapper>
            <ViewFileLink target="_blank" href={document.externalUrl} rel="noreferrer">
              <Typography.Text type="secondary">View external link</Typography.Text>
            </ViewFileLink>
          </LinkWrapper>
        )}
      </>
    );
  };

  return (
    <>
      <Flex gap={12}>
        <StyledSelect
          placeholder="Sort by Date"
          allowClear
          value={sortDate}
          onChange={handleDateSortChange}
          options={[
            { label: "Date (Ascending)", value: "asc" },
            { label: "Date (Descending)", value: "desc" },
          ]}
        />
        <StyledSelect
          placeholder="Sort by Type"
          allowClear
          value={sortType}
          onChange={handleTypeSortChange}
          options={[
            { label: "Type (A-Z)", value: "asc" },
            { label: "Type (Z-A)", value: "desc" },
          ]}
        />
      </Flex>
      <StyledList
        loading={isLoading}
        dataSource={sortedDocuments}
        bordered={false}
        renderItem={(document) => {
          // Type assertion: document has same structure as items in documents array
          const documentData = /** @type {typeof documents[0]} */ (document);
          return (
            <List.Item key={documentData.uuid}>
              <ListItemFlex justify="space-between" align="flex-start" flex={1} gap={32}>
                <LeftSectionFlex vertical>
                  <ListTitle>{formatDocumentType(documentData.type)}</ListTitle>
                  <ListSubtitle>{formatDate(documentData.createdAt)}</ListSubtitle>
                </LeftSectionFlex>
                <DocumentLinksContainer vertical align="flex-start" gap={4}>
                  {renderDocumentLink(documentData)}
                </DocumentLinksContainer>
              </ListItemFlex>
            </List.Item>
          );
        }}
      />
    </>
  );
}

export default DocumentsTable;
